import type { Appearance, NpcVisual } from '@jianghu/game-data';
import * as THREE from 'three';
import { QPose } from '../animation/pose';
import { merge, part, shade, sharedMaterials } from './geometry';
import { buildWeapon } from './weapons';

export const HUMANOID_JOINTS = [
  'hips',
  'spine',
  'chest',
  'neck',
  'head',
  'shoulderL',
  'elbowL',
  'handL',
  'shoulderR',
  'elbowR',
  'handR',
  'thighL',
  'kneeL',
  'footL',
  'thighR',
  'kneeR',
  'footR',
  'skirt',
  'hairTail',
] as const;
export type HumanoidJoint = (typeof HUMANOID_JOINTS)[number];

export interface HumanoidLook extends Partial<Pick<NpcVisual, 'beard' | 'hat' | 'prop'>> {
  appearance: Appearance;
  trimColor: string;
  accentColor: string;
  /** Tint of the equipped chest piece (adds a vest/overlay colour). */
  armorTint?: string | null;
  weaponModel?: string | null;
  weaponTint?: string;
  /** Bandits and other humanoid monsters. */
  style?: 'bandit';
}

/**
 * Procedurally built, rigged humanoid. Each joint is an Object3D in a skeleton hierarchy with all of
 * its rigid parts merged into a single vertex-coloured mesh. The model stands on y=0, faces +Z and is
 * ~1.8 m tall (female ~1.7 m). Animation writes joint rotations through `applyPose`.
 */
export class HumanoidRig {
  readonly root = new THREE.Group();
  /** Animated container (root offsets are applied here so `root` stays at the entity position). */
  readonly body = new THREE.Group();
  readonly joints = {} as Record<HumanoidJoint, THREE.Object3D>;
  readonly weaponSocket = new THREE.Group();
  readonly offHandSocket = new THREE.Group();
  private weapon: THREE.Object3D | null = null;
  private readonly meshes: THREE.Mesh[] = [];
  readonly height: number;

  constructor(readonly look: HumanoidLook) {
    const a = look.appearance;
    const female = a.gender === 'female';
    const s = female ? 0.95 : 1;
    const shoulderW = female ? 0.175 : 0.2;
    this.height = female ? 1.7 : 1.8;
    this.root.add(this.body);
    this.body.scale.setScalar(s);

    const robe = a.outfitColor;
    const trim = look.trimColor;
    const accent = look.accentColor;
    const skin = a.skinTone;
    const pants = look.style === 'bandit' ? '#3a3027' : shade(robe, -0.42);
    const boots = '#22191a';
    const armor = look.armorTint ?? null;
    const outfit = a.outfitStyle;

    const mk = (
      name: HumanoidJoint,
      parent: THREE.Object3D,
      pos: [number, number, number],
    ): THREE.Object3D => {
      const o = new THREE.Object3D();
      o.name = name;
      o.position.set(...pos);
      parent.add(o);
      this.joints[name] = o;
      return o;
    };
    const attach = (joint: THREE.Object3D, geos: THREE.BufferGeometry[]): void => {
      const mesh = new THREE.Mesh(merge(geos), sharedMaterials.cloth);
      mesh.castShadow = true;
      joint.add(mesh);
      this.meshes.push(mesh);
    };

    const hips = mk('hips', this.body, [0, 0.95, 0]);
    const spine = mk('spine', hips, [0, 0.12, 0]);
    const chest = mk('chest', spine, [0, 0.2, 0]);
    const neck = mk('neck', chest, [0, 0.22, 0]);
    const head = mk('head', neck, [0, 0.08, 0]);
    const shoulderL = mk('shoulderL', chest, [shoulderW, 0.17, 0]);
    const elbowL = mk('elbowL', shoulderL, [0, -0.28, 0]);
    const handL = mk('handL', elbowL, [0, -0.26, 0]);
    const shoulderR = mk('shoulderR', chest, [-shoulderW, 0.17, 0]);
    const elbowR = mk('elbowR', shoulderR, [0, -0.28, 0]);
    const handR = mk('handR', elbowR, [0, -0.26, 0]);
    const thighL = mk('thighL', hips, [0.095, -0.03, 0]);
    const kneeL = mk('kneeL', thighL, [0, -0.43, 0]);
    const footL = mk('footL', kneeL, [0, -0.42, 0]);
    const thighR = mk('thighR', hips, [-0.095, -0.03, 0]);
    const kneeR = mk('kneeR', thighR, [0, -0.43, 0]);
    const footR = mk('footR', kneeR, [0, -0.42, 0]);
    const skirt = mk('skirt', hips, [0, 0.06, 0]);

    // --- pelvis, sash and skirt
    attach(hips, [
      part.cyl(0.15, 0.14, 0.2, pants, { pos: [0, 0, 0] }),
      part.cyl(0.165, 0.165, 0.07, accent, { pos: [0, 0.1, 0] }),
      part.box(0.07, 0.16, 0.02, accent, { pos: [0.06, -0.02, 0.16], rot: [0.1, 0, 0.12] }),
    ]);
    const hem = outfit === 'scholar_gown' ? -0.8 : outfit === 'wanderer_coat' ? -0.42 : -0.58;
    const hemR = outfit === 'scholar_gown' ? 0.34 : outfit === 'wanderer_coat' ? 0.26 : 0.31;
    const skirtColor = armor && look.style !== 'bandit' ? robe : robe;
    attach(skirt, [
      part.lathe(
        [
          [0.16, 0.02],
          [0.2, -0.12],
          [hemR * 0.92, hem + 0.06],
          [hemR, hem],
        ],
        skirtColor,
        {},
        16,
      ),
      part.lathe(
        [
          [hemR * 0.995, hem + 0.05],
          [hemR + 0.006, hem],
        ],
        trim,
        {},
        16,
      ),
    ]);

    // --- torso
    attach(spine, [part.cyl(0.15, 0.155, 0.22, robe, { pos: [0, 0.1, 0] })]);
    const torso: THREE.BufferGeometry[] = [
      part.sphere(0.2, robe, { pos: [0, 0.08, 0], scale: [female ? 0.92 : 1, 1, 0.68] }),
      // Cross collar (left over right), the jiaoling.
      part.box(0.045, 0.25, 0.02, trim, { pos: [0.03, 0.1, 0.128], rot: [0.12, 0, -0.42] }),
      part.box(0.045, 0.2, 0.02, trim, { pos: [-0.045, 0.13, 0.122], rot: [0.12, 0, 0.42] }),
      part.cyl(0.07, 0.085, 0.06, trim, { pos: [0, 0.2, 0] }),
    ];
    if (armor) {
      // Equipped chest armour: a fitted vest over the robe in the armour's tint.
      torso.push(part.sphere(0.205, armor, { pos: [0, 0.05, 0], scale: [female ? 0.93 : 1.01, 0.82, 0.7] }));
      torso.push(part.box(0.3, 0.05, 0.03, shade(armor, -0.15), { pos: [0, -0.04, 0.13] }));
    }
    if (look.style === 'bandit') {
      torso.push(part.box(0.06, 0.42, 0.02, '#4a3020', { pos: [0, 0.06, 0.135], rot: [0.08, 0, 0.6] }));
    }
    attach(chest, torso);
    attach(neck, [part.cyl(0.045, 0.05, 0.1, skin, { pos: [0, 0.03, 0] })]);

    // --- head, face and hair
    const headParts: THREE.BufferGeometry[] = [
      part.sphere(0.12, skin, { pos: [0, 0.12, 0.005], scale: [0.95, 1.1, 1.0] }),
    ];
    const eyeY = 0.125;
    const eyeH = a.face === 'gentle' ? 1.25 : a.face === 'fierce' ? 0.8 : 1;
    headParts.push(part.sphere(0.017, '#1a1210', { pos: [0.042, eyeY, 0.104], scale: [1, eyeH, 0.5] }));
    headParts.push(part.sphere(0.017, '#1a1210', { pos: [-0.042, eyeY, 0.104], scale: [1, eyeH, 0.5] }));
    const browTilt = a.face === 'fierce' ? 0.35 : a.face === 'stern' ? 0.12 : a.face === 'gentle' ? -0.12 : 0;
    const browColor = shade(a.hairColor, 0.02);
    headParts.push(
      part.box(0.04, 0.009, 0.01, browColor, { pos: [0.044, eyeY + 0.03, 0.105], rot: [0, 0, -browTilt] }),
    );
    headParts.push(
      part.box(0.04, 0.009, 0.01, browColor, { pos: [-0.044, eyeY + 0.03, 0.105], rot: [0, 0, browTilt] }),
    );
    headParts.push(part.box(0.026, 0.006, 0.006, shade(skin, -0.25), { pos: [0, 0.063, 0.112] }));
    headParts.push(part.sphere(0.012, shade(skin, -0.06), { pos: [0, 0.095, 0.118] }));
    if (female) {
      headParts.push(part.sphere(0.014, '#e9a3a0', { pos: [0.06, 0.09, 0.096], scale: [1.4, 0.8, 0.3] }));
      headParts.push(part.sphere(0.014, '#e9a3a0', { pos: [-0.06, 0.09, 0.096], scale: [1.4, 0.8, 0.3] }));
    }
    if (look.beard) {
      headParts.push(
        part.cone(0.05, 0.2, '#e6e2d8', { pos: [0, -0.02, 0.09], rot: [Math.PI + 0.25, 0, 0] }, 8),
      );
      headParts.push(part.box(0.08, 0.012, 0.02, '#e6e2d8', { pos: [0, 0.075, 0.112] }));
    }
    const hair = a.hairColor;
    // Hair cap: a hemisphere tilted back so it frames the forehead and covers the crown and nape.
    headParts.push(
      part.hemi(
        0.13,
        hair,
        { pos: [0, 0.135, -0.012], rot: [-0.55, 0, 0], scale: [1.0, 1.0, 1.05] },
        Math.PI * 0.5,
      ),
    );
    headParts.push(part.sphere(0.112, hair, { pos: [0, 0.1, -0.05], scale: [1.0, 1.05, 0.85] }));
    switch (a.hairstyle) {
      case 'topknot':
        headParts.push(part.sphere(0.052, hair, { pos: [0, 0.27, -0.01] }));
        headParts.push(
          part.cyl(0.006, 0.006, 0.16, '#d4a54a', { pos: [0, 0.28, -0.01], rot: [0, 0, Math.PI / 2] }),
        );
        break;
      case 'twin_buns':
        headParts.push(part.sphere(0.05, hair, { pos: [0.085, 0.23, -0.02] }));
        headParts.push(part.sphere(0.05, hair, { pos: [-0.085, 0.23, -0.02] }));
        headParts.push(
          part.torus(0.04, 0.006, accent, { pos: [0.085, 0.23, -0.02], rot: [Math.PI / 2, 0, 0] }),
        );
        headParts.push(
          part.torus(0.04, 0.006, accent, { pos: [-0.085, 0.23, -0.02], rot: [Math.PI / 2, 0, 0] }),
        );
        break;
      case 'loose_long':
        headParts.push(part.box(0.22, 0.42, 0.06, hair, { pos: [0, -0.06, -0.09], rot: [0.12, 0, 0] }));
        headParts.push(part.box(0.05, 0.3, 0.05, hair, { pos: [0.1, 0.0, 0.0] }));
        headParts.push(part.box(0.05, 0.3, 0.05, hair, { pos: [-0.1, 0.0, 0.0] }));
        break;
      case 'braided_crown':
        headParts.push(
          part.torus(0.1, 0.022, hair, { pos: [0, 0.19, -0.01], rot: [Math.PI / 2 - 0.2, 0, 0] }),
        );
        headParts.push(part.sphere(0.015, '#e8d9a8', { pos: [0.07, 0.22, 0.05] }));
        break;
      case 'short_crop':
        break;
      case 'long_tail':
        headParts.push(part.torus(0.03, 0.01, accent, { pos: [0, 0.19, -0.11], rot: [0.6, 0, 0] }));
        break;
    }
    if (look.hat === 'straw') {
      headParts.push(part.cone(0.3, 0.13, '#d8bf7a', { pos: [0, 0.3, 0] }, 14));
    } else if (look.hat === 'guard_helmet') {
      headParts.push(part.hemi(0.14, '#5a5f66', { pos: [0, 0.15, 0] }, Math.PI * 0.5));
      headParts.push(part.cone(0.02, 0.1, '#a52a24', { pos: [0, 0.32, 0] }, 6));
    } else if (look.style === 'bandit') {
      headParts.push(part.cyl(0.13, 0.13, 0.035, '#8e2f2a', { pos: [0, 0.17, -0.005] }, 14));
      headParts.push(part.box(0.03, 0.14, 0.01, '#8e2f2a', { pos: [0.03, 0.12, -0.13], rot: [0.3, 0, 0.3] }));
    }
    attach(head, headParts);
    const tail = mk('hairTail', head, [0, 0.2, -0.11]);
    if (a.hairstyle === 'long_tail') {
      attach(tail, [part.cyl(0.03, 0.012, 0.4, hair, { pos: [0, -0.2, -0.02] })]);
    }

    // --- arms with wide sleeves
    const sleeveOpen = outfit === 'scholar_gown' ? 0.15 : outfit === 'wanderer_coat' ? 0.08 : 0.12;
    for (const side of ['L', 'R'] as const) {
      const sh = side === 'L' ? shoulderL : shoulderR;
      const el = side === 'L' ? elbowL : elbowR;
      const hd = side === 'L' ? handL : handR;
      attach(sh, [
        part.sphere(0.07, armor ?? robe, { pos: [0, -0.01, 0] }),
        part.cyl(0.058, 0.064, 0.29, robe, { pos: [0, -0.14, 0] }),
      ]);
      attach(el, [
        part.lathe(
          [
            [0.06, 0.02],
            [0.08, -0.1],
            [sleeveOpen, -0.24],
          ],
          robe,
          {},
          12,
        ),
        part.lathe(
          [
            [sleeveOpen - 0.004, -0.235],
            [sleeveOpen + 0.004, -0.25],
          ],
          trim,
          {},
          12,
        ),
        part.cyl(0.035, 0.03, 0.22, skin, { pos: [0, -0.13, 0] }),
      ]);
      attach(hd, [part.sphere(0.044, skin, { pos: [0, -0.04, 0.005], scale: [0.9, 1.25, 0.75] })]);
    }
    // Sockets: weapons extend along -Y (continuing the forearm). A slight forward cant reads better.
    this.weaponSocket.position.set(0, -0.055, 0.01);
    this.weaponSocket.rotation.set(-0.35, 0, 0);
    handR.add(this.weaponSocket);
    this.offHandSocket.position.set(0, -0.055, 0.01);
    handL.add(this.offHandSocket);

    // --- legs
    for (const side of ['L', 'R'] as const) {
      const th = side === 'L' ? thighL : thighR;
      const kn = side === 'L' ? kneeL : kneeR;
      const ft = side === 'L' ? footL : footR;
      attach(th, [part.cyl(0.075, 0.062, 0.45, pants, { pos: [0, -0.21, 0] })]);
      attach(kn, [
        part.cyl(0.058, 0.048, 0.42, pants, { pos: [0, -0.2, 0] }),
        part.cyl(0.054, 0.052, 0.14, shade(pants, 0.18), { pos: [0, -0.3, 0] }),
      ]);
      attach(ft, [part.box(0.095, 0.08, 0.22, boots, { pos: [0, -0.045, 0.045] })]);
    }

    this.setWeapon(look.weaponModel ?? null, look.weaponTint);
    if (look.prop) {
      const prop = buildWeapon(look.prop);
      if (prop) {
        prop.traverse((o) => {
          if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).castShadow = true;
        });
        (look.prop === 'basket' ? this.offHandSocket : this.weaponSocket).add(prop);
      }
    }
  }

  setWeapon(model: string | null, tint?: string): void {
    if (this.weapon) {
      this.weaponSocket.remove(this.weapon);
      disposeObject(this.weapon);
      this.weapon = null;
    }
    const w = buildWeapon(model, tint);
    if (w) {
      w.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).castShadow = true;
      });
      this.weaponSocket.add(w);
      this.weapon = w;
    }
  }

  /** World-space position of the weapon tip (for trails / VFX). */
  bladeTip(out: THREE.Vector3): THREE.Vector3 {
    return this.weaponSocket.localToWorld(out.set(0, -0.9, 0));
  }

  applyPose(q: QPose): void {
    for (const [name, quat] of q.joints) {
      const j = this.joints[name as HumanoidJoint];
      if (j) j.quaternion.copy(quat);
    }
    const r = q.root;
    this.body.position.set(r.rootX, r.rootY, r.rootZ);
    this.body.rotation.set(r.rootPitch, r.rootYaw, r.rootRoll, 'YXZ');
  }

  dispose(): void {
    for (const m of this.meshes) m.geometry.dispose();
    if (this.weapon) disposeObject(this.weapon);
  }
}

export function disposeObject(o: THREE.Object3D): void {
  o.traverse((c) => {
    const m = c as THREE.Mesh;
    if (m.isMesh) m.geometry.dispose();
  });
}
